const WAREHOUSE_ONLY_LINEHAUL_LABELS = ["LH", "NLH"];

const isWarehouseOnlyLinehaul = (orderLabel = "") =>
  WAREHOUSE_ONLY_LINEHAUL_LABELS.includes(orderLabel);

const isCustomerBookendTerminal = (terminal) => {
  const locTypeUpper = String(terminal?.type_of_loc || "").toUpperCase();
  return locTypeUpper === "PICKUP" || locTypeUpper === "DELIVERY";
};

const isTerminalComplete = (terminal) => {
  const status = String(terminal?.tracking_status || terminal?.status || "").toUpperCase();
  if (status === "COMPLETED" || status === "COMPLETE" || status === "DEPARTED" || status === "DEP") {
    return true;
  }
  return Boolean(terminal?.actual_departure || terminal?.departure_date || terminal?.departure_time);
};

const routingTerminals = (terminals = [], orderLabel = "") => {
  const sorted = [...(Array.isArray(terminals) ? terminals : [])]
    .sort((a, b) => (a.sequence_no ?? 0) - (b.sequence_no ?? 0));
  if (isWarehouseOnlyLinehaul(orderLabel)) return sorted;
  return sorted.filter((terminal) => !isCustomerBookendTerminal(terminal));
};

const warehouseIdByCode = (warehouseHash = {}, code) => {
  if (!code) return null;
  const upper = String(code).trim().toUpperCase();
  return Object.values(warehouseHash || {}).find((wh) => (
    String(wh?.location_code || wh?.code || "").trim().toUpperCase() === upper
  ))?.id || null;
};

const codesEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((code, idx) => (
    String(code || "").trim().toUpperCase() === String(right[idx] || "").trim().toUpperCase()
  ));
};

const pathCodes = (path) => path?.remainingCodes || path?.routeCodes || [];

const startsWithCodes = (longer = [], shorter = []) => (
  shorter.length > 0
  && longer.length >= shorter.length
  && codesEqual(shorter, longer.slice(0, shorter.length))
);

export const getCompletedPrefixCodes = (terminals = [], orderLabel = "") => {
  const codes = [];
  for (const terminal of routingTerminals(terminals, orderLabel)) {
    if (!isTerminalComplete(terminal)) break;
    const code = String(terminal.warehouse_code || terminal.warehouse_name || "").trim();
    if (code) codes.push(code);
  }
  return codes;
};

export const resolveCompletedPrefixCodes = (terminals = [], orderLabel = "", fallbackCodes = []) => {
  const fromTerminals = getCompletedPrefixCodes(terminals, orderLabel);
  if (fromTerminals.length) return fromTerminals;
  return (Array.isArray(fallbackCodes) ? fallbackCodes : [])
    .map((code) => String(code || "").trim())
    .filter(Boolean);
};

export const getRemainingRouteCodes = (terminals = [], orderLabel = "") => {
  const codes = routingTerminals(terminals, orderLabel)
    .map((terminal) => String(terminal.warehouse_code || terminal.warehouse_name || "").trim())
    .filter(Boolean);
  return codes.slice(getCompletedPrefixCodes(terminals, orderLabel).length);
};

export const resolveCurrentLaneOriginId = (
  terminals = [],
  fallbackPickupWhId = null,
  warehouseHash = {},
  orderLabel = ""
) => {
  const warehouses = routingTerminals(terminals, orderLabel);
  if (!warehouses.length) return fallbackPickupWhId || null;
  const current = warehouses.find((terminal) => !isTerminalComplete(terminal)) || warehouses[warehouses.length - 1];
  return current?.warehouse_id
    || warehouseIdByCode(warehouseHash, current?.warehouse_code || current?.warehouse_name)
    || fallbackPickupWhId
    || null;
};

export const mergePrefixCodes = (prefixCodes = [], remainingCodes = []) => {
  const prefix = (prefixCodes || []).map((code) => String(code).trim()).filter(Boolean);
  const remaining = (remainingCodes || []).map((code) => String(code).trim()).filter(Boolean);
  if (!prefix.length) return remaining;
  if (!remaining.length) return prefix;
  if (prefix[prefix.length - 1].toUpperCase() === remaining[0].toUpperCase()) {
    return [...prefix.slice(0, -1), ...remaining];
  }
  return [...prefix, ...remaining];
};

export const composeRouteCodes = (routeOrCodes, prefixCodes = []) => {
  const fromRoute = Array.isArray(routeOrCodes)
    ? routeOrCodes.map((code) => String(code || "").trim()).filter(Boolean)
    : String(routeOrCodes || "").split("-").map((part) => part.trim()).filter(Boolean);
  if (prefixCodes.length && startsWithCodes(fromRoute, prefixCodes)) return fromRoute;
  return mergePrefixCodes(prefixCodes, fromRoute);
};

export const attachCompletedPrefixToLane = (lane, prefixCodes = []) => {
  if (!lane) return lane;
  const fromRoute = Array.isArray(lane.routeCodes) && lane.routeCodes.length
    ? lane.routeCodes.filter(Boolean)
    : String(lane.route || "").split("-").map((part) => part.trim()).filter(Boolean);
  const remainingCodes = Array.isArray(lane.remainingCodes) && lane.remainingCodes.length
    ? lane.remainingCodes.filter(Boolean)
    : (prefixCodes.length && startsWithCodes(fromRoute, prefixCodes)
      ? fromRoute.slice(prefixCodes.length)
      : fromRoute);
  const displayCodes = mergePrefixCodes(prefixCodes, remainingCodes);
  return {
    ...lane,
    remainingCodes,
    remainingRoute: remainingCodes.join("-"),
    routeCodes: displayCodes,
    route: displayCodes.join("-") || lane.route,
  };
};

export const prefixLanePaths = (paths = [], terminals = [], orderLabel = "", fallbackCodes = []) => {
  const prefixCodes = resolveCompletedPrefixCodes(terminals, orderLabel, fallbackCodes);
  return (Array.isArray(paths) ? paths : []).map((path) => (
    attachCompletedPrefixToLane(path, prefixCodes)
  ));
};

export const findPreferredRemainingPath = (paths = [], savedPathId, remainingCodes = []) => {
  const list = Array.isArray(paths) ? paths : [];
  if (remainingCodes.length) {
    const exact = list.find((path) => codesEqual(pathCodes(path), remainingCodes));
    if (exact) return exact;
    const extension = list
      .filter((path) => startsWithCodes(pathCodes(path), remainingCodes))
      .sort((left, right) => pathCodes(right).length - pathCodes(left).length)[0];
    if (extension) return extension;
  }
  if (savedPathId) {
    const saved = list.find((path) => String(path.path_id) === String(savedPathId));
    if (saved) return saved;
  }
  if (remainingCodes.length) {
    return list.find((path) => path.isCurrentTracking) || null;
  }
  return list.find((path) => path.is_primary) || list[0] || null;
};

export const buildCurrentTrackingLane = (terminals = [], orderLabel = "", savedPathId = null) => {
  const remainingCodes = getRemainingRouteCodes(terminals, orderLabel);
  const prefixCodes = getCompletedPrefixCodes(terminals, orderLabel);
  if (!remainingCodes.length && !prefixCodes.length) return null;
  return attachCompletedPrefixToLane({
    path_id: savedPathId || "current-tracking-path",
    route: remainingCodes.join("-"),
    routeCodes: remainingCodes,
    is_primary: false,
    isCurrentTracking: true,
    legs: [],
  }, prefixCodes);
};

export const withCurrentTrackingPath = (paths = [], terminals = [], orderLabel = "", savedPathId = null) => {
  const remainingCodes = getRemainingRouteCodes(terminals, orderLabel);
  let list = Array.isArray(paths) ? [...paths] : [];
  const matched = findPreferredRemainingPath(list, null, remainingCodes);
  const hasTrackingMatch = Boolean(
    matched
    && remainingCodes.length
    && (
      codesEqual(pathCodes(matched), remainingCodes)
      || startsWithCodes(pathCodes(matched), remainingCodes)
    )
  );
  if (!hasTrackingMatch) {
    const current = buildCurrentTrackingLane(terminals, orderLabel, savedPathId);
    if (
      current
      && !list.some((path) => codesEqual(pathCodes(path), current.remainingCodes || []))
    ) {
      list = [current, ...list];
    }
  }
  return {
    paths: list,
    preferred: findPreferredRemainingPath(list, savedPathId, remainingCodes),
  };
};
